import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Spinner } from "@/components/States";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, X, Save, Loader2, Star, Check } from "lucide-react";

const EMPTY = {
  name: "", badge_label: "", color: "#E5A400", rank: 1,
  is_featured: false, benefits: [], order: 99, is_active: true,
};

function MembershipForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    ...EMPTY,
    ...(initial || {}),
    benefitsText: (initial?.benefits || []).join("\n"),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const payload = {
      name: form.name, badge_label: form.badge_label, color: form.color,
      rank: parseInt(form.rank) || 1, is_featured: form.is_featured,
      benefits: form.benefitsText.split("\n").map((s) => s.trim()).filter(Boolean),
      order: parseInt(form.order) || 0, is_active: form.is_active,
    };
    try {
      if (initial?.id) await api.put(`/admin/memberships/${initial.id}`, payload);
      else await api.post("/admin/memberships", payload);
      toast.success(initial?.id ? "Membresía actualizada" : "Membresía creada");
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo guardar");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex justify-center overflow-y-auto bg-black/50 p-0 sm:p-4">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="h-fit min-h-screen w-full max-w-lg bg-white sm:min-h-0 sm:rounded-2xl" data-testid="membership-form">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-black/5 bg-white px-5 py-4">
          <h2 className="font-display text-xl font-bold">{initial?.id ? "Editar nivel" : "Nuevo nivel"}</h2>
          <button onClick={onClose} data-testid="membership-form-close" className="rounded-full bg-[#F3F0E6] p-2"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4 p-5">
          <div className="flex items-center gap-3">
            <span className="rounded-full px-3 py-1 text-xs font-black uppercase text-white" style={{ backgroundColor: form.color }}>
              {form.badge_label || form.name || "NIVEL"}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-bold text-[#5C5C5C]">Nombre *</span>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="membership-name" className="rounded-lg border border-black/10 px-3 py-2 text-sm outline-none focus:border-[#E54D2E]" placeholder="Ej. Diamante" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-bold text-[#5C5C5C]">Insignia</span>
              <input value={form.badge_label} onChange={(e) => set("badge_label", e.target.value)} data-testid="membership-badge" className="rounded-lg border border-black/10 px-3 py-2 text-sm outline-none" placeholder="Ej. DIAMANTE" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-bold text-[#5C5C5C]">Color</span>
              <input type="color" value={form.color} onChange={(e) => set("color", e.target.value)} data-testid="membership-color" className="h-10 w-full rounded-lg border border-black/10" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-bold text-[#5C5C5C]">Prioridad (rank)</span>
              <input type="number" value={form.rank} onChange={(e) => set("rank", e.target.value)} data-testid="membership-rank" className="rounded-lg border border-black/10 px-3 py-2 text-sm outline-none" />
              <span className="text-[10px] text-[#9c9c9c]">Mayor número = aparece primero</span>
            </label>
          </div>

          <label className="flex items-center gap-2 rounded-xl bg-[#FFF7E6] px-4 py-3 text-sm font-semibold">
            <input type="checkbox" checked={form.is_featured} onChange={(e) => set("is_featured", e.target.checked)} data-testid="membership-featured" />
            <Star size={15} className="fill-[#FFB800] text-[#FFB800]" />
            Aparecen como "Negocios Destacados"
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-bold text-[#5C5C5C]">Beneficios (uno por línea)</span>
            <textarea value={form.benefitsText} onChange={(e) => set("benefitsText", e.target.value)} rows={5} data-testid="membership-benefits" className="rounded-lg border border-black/10 px-3 py-2 text-sm outline-none focus:border-[#E54D2E]" placeholder={"Máxima visibilidad\nGalería de fotos\nAparece primero"} />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-bold text-[#5C5C5C]">Orden</span>
              <input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className="rounded-lg border border-black/10 px-3 py-2 text-sm outline-none" />
            </label>
            <label className="mt-6 flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} /> Activo
            </label>
          </div>

          {error && <p className="text-sm font-semibold text-[#E54D2E]" data-testid="membership-form-error">{error}</p>}
          <button type="submit" disabled={saving} data-testid="membership-form-submit" className="flex items-center justify-center gap-2 rounded-xl bg-[#E54D2E] py-3.5 font-bold text-white disabled:opacity-60">
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Guardar nivel
          </button>
        </form>
      </motion.div>
    </div>
  );
}

export default function AdminMemberships() {
  const { refreshMemberships } = useData();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/memberships", { params: { include_inactive: true } }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (m) => {
    if (!window.confirm(`¿Eliminar el nivel "${m.name}"? Los negocios con este nivel quedarán sin membresía.`)) return;
    await api.delete(`/admin/memberships/${m.id}`);
    toast.success("Nivel eliminado");
    load(); refreshMemberships();
  };

  const onSaved = () => { setFormOpen(false); load(); refreshMemberships(); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold">Niveles de membresía</h2>
          <p className="text-xs text-[#5C5C5C]">Define planes, beneficios y visibilidad de los negocios.</p>
        </div>
        <button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-membership-button" className="flex items-center gap-1.5 rounded-xl bg-[#E54D2E] px-4 py-2.5 text-sm font-bold text-white">
          <Plus size={18} /> Nuevo
        </button>
      </div>

      {loading ? <Spinner /> : (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {items.map((m) => (
            <div key={m.id} data-testid={`membership-row-${m.slug}`} className="rounded-2xl border border-black/5 bg-white p-4">
              <div className="flex items-center justify-between">
                <span className="rounded-full px-3 py-1 text-xs font-black uppercase text-white" style={{ backgroundColor: m.color }}>
                  {m.badge_label || m.name}
                </span>
                <div className="flex items-center gap-1">
                  {m.is_featured && <Star size={15} className="fill-[#FFB800] text-[#FFB800]" />}
                  <button onClick={() => { setEditing(m); setFormOpen(true); }} data-testid={`edit-membership-${m.slug}`} className="rounded-lg p-2 hover:bg-[#F3F0E6]"><Pencil size={15} /></button>
                  <button onClick={() => remove(m)} data-testid={`delete-membership-${m.slug}`} className="rounded-lg p-2 hover:bg-red-50"><Trash2 size={15} className="text-[#E54D2E]" /></button>
                </div>
              </div>
              <p className="mt-2 text-sm font-bold text-[#1A1A1A]">{m.name} · prioridad {m.rank}</p>
              <ul className="mt-2 flex flex-col gap-1">
                {m.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-[#5C5C5C]">
                    <Check size={13} className="mt-0.5 shrink-0 text-[#16A34A]" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {formOpen && <MembershipForm initial={editing} onClose={() => setFormOpen(false)} onSaved={onSaved} />}
    </div>
  );
}
